// src/utils/MarkdownRenderer.js
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Titles we want to always render as headings (case-insensitive)
const H2_TITLES = [
  "Strategic Reframe",
  "Strategic Summary",
  "Clarify the Core Need",
  "Fresh Strategic Angles",
  "Risks and Reality Checks",
  "Success Criteria & Critical Assumptions",
  "Critical Assumptions",
  "Suggested Next Moves",
  "Next Moves",
  "Final Take",
  "Provocation",
  "Provocative Question",
];

const H3_TITLES = [
  "Key Metric",
  "Core Metric",
  "Assumptions to Test",
  "Stress-Test Threshold",
  "Success Scenario",
  "Failure Scenario",
  "Failure Mode",
  "Start",
  "Test",
  "Expand",
];

function toHeadingRegex(titles) {
  return new RegExp(
    `^\\s*(?:${titles.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\s*:??\\s*$`,
    "gmi"
  );
}

function normalizeHeadings(markdown = "") {
  const h2rx = toHeadingRegex(H2_TITLES);
  const h3rx = toHeadingRegex(H3_TITLES);
  return markdown
    .replace(h2rx, m => `\n\n### ${m.trim().replace(/:$/, "")}\n\n`)
    .replace(h3rx, m => `\n\n#### ${m.trim().replace(/:$/, "")}\n\n`);
}

/** Make bare domains/URLs clickable, and shorten standalone autolink lines */
function autoLinkToMarkdown(text = "") {
  // 1) Autolink bare domains/URLs
  const domainOrUrl =
    /\b(https?:\/\/[^\s<>()]+|(?:[a-z0-9-]+\.)+[a-z]{2,})(?![^<]*>)(?![^[]*\))/gi;

  let out = text.replace(domainOrUrl, (match) => {
    const url = match.startsWith("http") ? match : `https://${match}`;
    return `<${url}>`;
  });

  // 2) If a whole line is just <https://…>, shorten to a labeled link
  out = out.replace(
    /^\s*<((?:https?:\/\/)[^>]+)>\s*$/gmi,
    (_m, url) => `[source](${url})`
  );

  return out;
}


export const MarkdownRenderer = ({ content = "" }) => {
  const processed = useMemo(
    () => autoLinkToMarkdown(normalizeHeadings(content)),
    [content]
  );

  return (
    <div className="max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}

        components={{
          pre: ({ node, children, ...props }) => (
   <pre
     className="overflow-x-auto whitespace-pre-wrap break-words my-3 p-3 rounded-md bg-gray-900/60 border border-white/10"
     {...props}
   >
     {children}
   </pre>
 ),
 code: ({ inline, children, ...props }) =>
   inline ? (
     <code className="px-1 py-0.5 rounded bg-white/10 [overflow-wrap:anywhere]" {...props}>
       {children}
     </code>
   ) : (
     <code className="[overflow-wrap:anywhere]" {...props}>{children}</code>
   ),
          a: ({ node, children, href = "", ...props }) => {
            // Fallback label so the anchor always has accessible content
            const childArray = React.Children.toArray(children);
            const hasTextChild = childArray.some(c =>
              typeof c === "string" ? c.trim().length > 0 : !!c
            );
            const label = hasTextChild ? children : href;

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 underline hover:text-blue-300 visited:text-blue-500/80 underline-offset-2 break-words"
                {...props}
              >
                {label}
              </a>
            );
          },
          h3: ({ node, children }) => (
            <h3 className="text-purple-300 font-semibold text-xl md:text-2xl mt-1 mb-2">
              {children}
            </h3>
          ),
          h4: ({ node, children }) => (
            <h4 className="text-purple-300 font-semibold text-lg mt-5 mb-2">
              {children}
            </h4>
          ),
          p: ({ node, children }) => (
            <p className="mt-0 mb-3 leading-7">{children}</p>
          ),
          strong: ({ node, ...props }) => (
            <strong className="text-purple-300 font-semibold" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-6 my-3 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-6 my-3 space-y-1" {...props} />
          ),
          li: ({ node, children, ...props }) => (
            <li className="leading-7" {...props}>{children}</li>
          ),
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
};
